import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../../../redux/store";
import { authAndRegSelector } from "../../../../redux/profile/authAndReg/selectors";
import { fetchReg } from "../../../../redux/profile/authAndReg/asyncActions";

import {
  NameInput,
  EmailInput,
  PhoneInput,
  PasswordInput,
  Button,
  CheckboxInput,
} from "../../../../components";
import { cleanPhone } from "../../../../utils";
import { addEmail, addName, addPassword, addPhone } from "../../../../redux/profile/settings/changePass/slice";

import styles from "./RegisterForm.module.scss";

import { ValidMessages } from "../../../../resources/locale";
import { RegValuesType } from "./types";

type RegisterFormProps = {
  handleSubmitForm?: () => void;
};

export const RegisterForm: React.FC<RegisterFormProps> = (props) => {
  const { handleSubmitForm } = props;
  const { isLoading, errorMessage } = useSelector(authAndRegSelector);
  const dispatch = useAppDispatch();

  const initialValues: RegValuesType = {
    name: "",
    email: "",
    phone: "",
    password: "",
    passwordRepeat: "",
    acceptRules: false,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(ValidMessages.inputs.name.REQUIRED),
    email: Yup.string()
      .email(ValidMessages.inputs.email.INCORRECT)
      .required(ValidMessages.inputs.email.REQUIRED),
    phone: Yup.string()
      .min(18, ValidMessages.inputs.phone.NOT_FULL)
      .required(ValidMessages.inputs.phone.REQUIRED),
    password: Yup.string()
      .required(ValidMessages.inputs.password.REQUIRED)
      .min(6, ValidMessages.inputs.password.NOT_FULL),
    passwordRepeat: Yup.string()
      .required(ValidMessages.inputs.passwordRepeat.REQUIRED)
      .oneOf([Yup.ref("password"), null], ValidMessages.inputs.passwordRepeat.NOT_MATCH),
  });

  const handleSubmit = async (values: RegValuesType, formikActions: any) => {
    try {
      const phone = cleanPhone(values.phone);

      const data = {
        values: {
          name: values.name,
          email: values.email,
          phone: phone,
          password: values.password,
          passwordRepeat: values.passwordRepeat,
          acceptRules: values.acceptRules,
        },
        actions: formikActions,
      };

      await dispatch(fetchReg(data)).unwrap();

      dispatch(addPhone(phone));
      dispatch(addName(values.name));
      dispatch(addEmail(values.email));
      dispatch(addPassword({
        password: values.password,
        passwordRepeat: values.passwordRepeat,
      }));

      handleSubmitForm && handleSubmitForm();
    } catch (error: any) {
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => handleSubmit(values, actions)}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {(formik) => {
        return (
          <Form className={styles.form}>
            <div className={styles.formSection}>
              <NameInput
                name="name"
                lable="ФИО"
                placeholder="Иванов Иван"
              />
              <ErrorMessage component="div" className={styles.errorMessage} name="name" />
            </div>
            <div className={styles.formSection}>
              <EmailInput
                name="email"
                lable="Email (для чеков при оплате)"
                placeholder="example@example.com"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="email"
              />
            </div>
            <div className={styles.formSection}>
              <PhoneInput
                name="phone"
                lable="Телефон"
                placeholder="+7 (___) ___-__-__"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="phone"
              />
            </div>
            <div className={styles.formSection}>
              <PasswordInput
                name="password"
                placeholder="Пароль"
                lable="Пароль"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="password"
              />
            </div>
            <div className={styles.formSection}>
              <PasswordInput
                name="passwordRepeat"
                placeholder="Повторите пароль"
                lable="Повторите пароль"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="passwordRepeat"
              />
              {errorMessage ? (
                <div className={styles.errorMessage}>
                  {errorMessage.includes('Not allowed')
                    ? "Сервер временно недоступен"
                    : errorMessage}
                </div>
              ) : null}
            </div>

            <div className={styles.checkboxSection}>
              <CheckboxInput
                name="acceptRules"
                label="Нажимая на кнопку Вы подтверждаете, что ознакомлены и согласны с условиями Политики обработки персональных данных и правилам клуба."
                links={[
                  { text: "Политики обработки персональных данных", to: "/politics" },
                  { text: "правилам клуба", to: "/rules" }
                ]}
              />
            </div>

            <Button
              type="submit"
              newStyles={styles.button}
              disabled={formik.isSubmitting || isLoading}
            >
              {isLoading ? "Регистрация..." : "Зарегистрироваться"}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
